import { MobXProviderContext } from "mobx-react";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CLIENT_ACCECEPTENCE_CRITERIA, CLIENT_NON_ACCECEPTENCE_CRITERIA, getErrorTitle } from "../../../constants/constants";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { DropDownOption } from "../../../models/primereact";
import { ClientAvailabilityProfile } from "../../../models/businessHomeProfile";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appFullDialogStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

interface ClientAvailabilityProfileModalProps {
  clientAvailabilityProfileData: ClientAvailabilityProfile | undefined;
  homeId: string | undefined;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
}

const ClientAvailabilityProfileModal: FC<ClientAvailabilityProfileModalProps> = ({
  isOpen,
  toggleModal,
  clientAvailabilityProfileData,
  homeId,
  flag,
  setFlag
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const defaultValues = {
    homeId: homeId || "",
    description: "",
    noOfRoomsAvailable: 0,
    acceptanceCriterias: [] as DropDownOption[],
    nonAcceptanceCriterias: [] as DropDownOption[],
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  useEffect(() => {
    const fetchProfileDetails = async () => {
      if (!clientAvailabilityProfileData) return;
      setLoading(true);
      try {
        reset({
          homeId: clientAvailabilityProfileData.homeId,
          description: clientAvailabilityProfileData.description,
          noOfRoomsAvailable: clientAvailabilityProfileData.noOfRoomsAvailable,
          acceptanceCriterias: clientAvailabilityProfileData.acceptanceCriterias,
          nonAcceptanceCriterias: clientAvailabilityProfileData.nonAcceptanceCriterias,
        });
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoading(false);
      }
    };

    fetchProfileDetails();
  }, [clientAvailabilityProfileData]);

  const onSubmit = async (data: any) => {
    const tempData = {
      ...data,
      homeId: clientAvailabilityProfileData ? clientAvailabilityProfileData.homeId : homeId,
    };

    setErrorMessage(null);
    setLoading(true);
    try {
      await backendAPIStore.init();
      if (!clientAvailabilityProfileData) {
        await backendAPIStore.addClientAvailabilityProfile(tempData);
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Client Availability Profile created successfully!', life: 3000 });
      } else {
        await backendAPIStore.updateClientAvailabilityProfile(tempData);
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Client Availability Profile updated successfully!', life: 3000 });
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to update', life: 3000 });
    } finally {
      reset();
      toggleModal(false);
      setLoading(false);
      setFlag(!flag);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = renderSurfaceHeader(
    "Client Availability",
    "Describe openings, acceptance criteria, and room availability for prospective residents.",
    "pi pi-building",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading,
    onCancel: () => {
      toggleModal(false);
      reset();
    },
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Profile",
  });

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appFullDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle} closeOnEscape={false}
      onHide={() => { if (!isOpen) return; toggleModal(false); reset(); }}>
      <Toast ref={toast} />
      <form>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Availability overview</div>
          <div className="mt-2" style={appMutedTextStyle}>
            Share what kind of resident needs the home can support and how many rooms are currently open.
          </div>
        </div>
        <div style={appFormSectionStyle}>
        <div className="field">
          <span className="p-float-label">
            <Controller name="description" control={control} rules={{
              required: 'Description is required.',
              maxLength: { value: 1000, message: 'Description must be at most 1000 characters long.' },
            }} render={({ field, fieldState }) => (
              <InputTextarea rows={5} style={{ width: "100%" }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="description" className={classNames({ 'p-error': errors.description })}>Description</label>
          </span>
          {getFormErrorMessage('description')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="noOfRoomsAvailable" control={control} rules={{
              required: 'Number of Rooms Available is required.',
              min: { value: 1, message: 'Number of Rooms Available must be at least 1.' },
              max: { value: 8, message: 'Number of Rooms Available must be at most 8.' },
            }} render={({ field, fieldState }) => (
              <InputNumber id={field.name} {...field} onChange={(e) => field.onChange(e.value)}
                className={classNames({ 'p-invalid': fieldState.invalid })} style={{ width: "100%" }} />
            )} />
            <label htmlFor="noOfRoomsAvailable" className={classNames({ 'p-error': errors.noOfRoomsAvailable })}>Number of Rooms Available</label>
          </span>
          {getFormErrorMessage('noOfRoomsAvailable')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="acceptanceCriterias" control={control} rules={{
              required: 'At least one skill is required.',
            }} render={({ field, fieldState }) => (
              <MultiSelect id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }} multiple
                placeholder="Select Acceptance Criterias" display="chip" title="Select Acceptance Criterias" selectAll={true} selectAllLabel="Select All"
                onChange={(e) => field.onChange(e.value)} options={CLIENT_ACCECEPTENCE_CRITERIA} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="acceptanceCriterias" className={classNames({ 'p-error': errors.acceptanceCriterias })}>Acceptance Criterias</label>
          </span>
          {getFormErrorMessage('acceptanceCriterias')}
        </div>
        <div className="field">
          <span className="p-float-label">
            <Controller name="nonAcceptanceCriterias" control={control} rules={{
              required: 'At least one certification is required.',
            }} render={({ field, fieldState }) => (
              <MultiSelect id={field.name} value={field.value} optionLabel="name" style={{ width: '100%' }} multiple
                placeholder="Select Non-Acceptance Criterias" display="chip" title="Select Non-Acceptance Criterias" selectAll={true} selectAllLabel="Select All"
                onChange={(e) => field.onChange(e.value)} options={CLIENT_NON_ACCECEPTENCE_CRITERIA} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="nonAcceptanceCriterias" className={classNames({ 'p-error': errors.nonAcceptanceCriterias })}>Non-Acceptance Criterias</label>
          </span>
          {getFormErrorMessage('nonAcceptanceCriterias')}
        </div>
        </div>
      </form>
    </Dialog>
  );
};

export default ClientAvailabilityProfileModal;
